import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import * as shared from '../../shared';
import { ElectricityMeterUnitEntry, EmuEntryInfo } from '../charging-stations-section.model';
import { EmuEntryService } from "../services/emu-entry.service";


export enum RelatedProductsState
{
  IsUpToDate,
  IsOutdated,
  IsDeleted
}

@Component({
  selector: 'app-electricity-meter-unit',
  templateUrl: './electricity-meter-unit.component.html',
  styleUrls: ['./electricity-meter-unit.component.scss']
})
export class ElectricityMeterUnitComponent extends shared.EditableSectionPartComponent implements OnInit, OnDestroy, OnChanges {
  @Input() electricityMeterUnits: shared.Product[];
  @Input() isMarkedForDeletion: boolean;
  @Input() electricityMeterUnitInfo: EmuEntryInfo;
  @Output() removeElectricityMeterUnitRequest = new EventEmitter();
  @Output() updatedElectricityMeterUnit = new EventEmitter<ElectricityMeterUnitEntry>();
  currentElectricityMeterUnit: shared.Product;
  displayedColumns: string[] = [ 'productId', 'quantity', 'discountFactor', 'description', 'pricePerUnit', 'ap', 'totalCost', 'delete'];
  dataSource: any[];
  relatedProductsStates = RelatedProductsState;
  getEntryTotalCosts = shared.getEntryTotalCosts;
  private relatedProductState: RelatedProductsState;
  private internalDiscountFactor: number;
  private internalIsAlternative: boolean;
  private internalDisplayProductUpdates: boolean;

  public get discountFactor(): number {
    return this.internalDiscountFactor;
  }

  public get isAlternative(): boolean {
    return this.internalIsAlternative;
  }

  public get displayProductUpdates(): boolean {
    return this.internalDisplayProductUpdates;
  }

  public get relatedProductIsUpToDate(): boolean {
    return this.relatedProductState === RelatedProductsState.IsUpToDate;
  }

  public get relatedProductWasDeleted(): boolean {
    return this.relatedProductState === RelatedProductsState.IsDeleted;
  }

  public get relatedProductWasUpdated(): boolean {
    return this.relatedProductState === RelatedProductsState.IsOutdated;
  }

  public get latestProduct(): shared.Product {
    return this.getElectricityMeterUnit(this.currentElectricityMeterUnit.id);
  }

  constructor(
    private emuEntryService: EmuEntryService,
    private totalCostsService: shared.TotalCostsService,
    protected element: ElementRef<Element>,
    protected csBuildupStore: shared.CsBuildupStateStore,
    protected csBuildupStateQuery: shared.CsBuildupStateQuery) {
    super(element, csBuildupStore, csBuildupStateQuery);
  }

  ngOnInit(): void {
    super.ngOnInit();
    const emu = shared.createProduct(this.electricityMeterUnitInfo.emuEntry);
    this.internalIsAlternative = this.electricityMeterUnitInfo.emuEntry.isAlternative;
    this.setCurrentElectricityMeterUnit(emu, this.electricityMeterUnitInfo.emuEntry.discountFactor);
    this.dataSource = [this.electricityMeterUnitInfo];
    this.relatedProductState = this.getRelatedProductState(this.currentElectricityMeterUnit);
    if (this.electricityMeterUnitInfo.isNew) {
      this.emuEntryService.updateOnlyInStore(this.electricityMeterUnitInfo.emuEntry);
      super.startEdit();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.electricityMeterUnitsMarkedForDeletion && !!this.currentElectricityMeterUnit) {
      this.publishTotalCosts();
    }

    if (changes.electricityMeterUnits && !!this.currentElectricityMeterUnit) {
      this.relatedProductState = this.getRelatedProductState(this.currentElectricityMeterUnit);
    }
  }

  ngOnDestroy(): void {
    this.totalCostsService.removePartialCosts(this.electricityMeterUnitInfo.emuEntry.id.toString());
    super.ngOnDestroy();
  }

  onElectricityMeterUnitSelected(electricityMeterUnitId: number): void {
    const emu = this.electricityMeterUnits.find(p => p.id === electricityMeterUnitId);
    this.setCurrentElectricityMeterUnit(emu);
  }

  onDiscountFactorChanged(discountFactor: number): void {
    this.internalDiscountFactor = discountFactor;
    this.publishTotalCosts();
  }

  onIsAlternativeChanged(isAlternative: boolean): void {
    this.internalIsAlternative = isAlternative;
    this.publishTotalCosts();
  }

  onDisplayProductUpdatesChanged(value: boolean): void {
    this.internalDisplayProductUpdates = value;
    this.publishTotalCosts();
  }


  saveChanges(): void {
    const newEmuEntry =  {
      ...this.electricityMeterUnitInfo.emuEntry,
      productId: this.currentElectricityMeterUnit.id,
      description: this.currentElectricityMeterUnit.name,
      pricePerUnit: this.currentElectricityMeterUnit.pricePerUnit,
      productVersion: this.currentElectricityMeterUnit.version,
      discountFactor: this.discountFactor,
      isAlternative: this.isAlternative
    };
    if (this.electricityMeterUnitInfo.isNew) {
      this.emuEntryService.add(this.electricityMeterUnitInfo.projectId, newEmuEntry).subscribe(
        addedEmuEntry => {
          this.updatedElectricityMeterUnit.emit(addedEmuEntry);
        },
        error => {},
        () => this.onChangesSaved());
    }
    else {
      this.emuEntryService.update(this.electricityMeterUnitInfo.projectId, newEmuEntry).subscribe(
        updatedEmuEntry => this.updatedElectricityMeterUnit.emit(updatedEmuEntry),
        error => {},
        () => this.onChangesSaved());
    }
  }

  deleteElectricityMeterUnit(): void {
    this.removeElectricityMeterUnitRequest.emit();
  }

  discardChanges(): void {
    if (this.electricityMeterUnitInfo.isNew) {
      this.removeElectricityMeterUnitRequest.emit();
    } else {
      const emu = shared.createProduct(this.electricityMeterUnitInfo.emuEntry);
      this.setCurrentElectricityMeterUnit(emu, this.electricityMeterUnitInfo.emuEntry.discountFactor);
      this.relatedProductState = this.getRelatedProductState(this.currentElectricityMeterUnit);
      this.internalIsAlternative = this.electricityMeterUnitInfo.emuEntry.isAlternative;
    }
    super.endEdit();
  }

  alignToCurrentProduct(): void {
    const emu = this.getElectricityMeterUnit(this.currentElectricityMeterUnit.id);
    this.setCurrentElectricityMeterUnit(emu);
    this.internalDisplayProductUpdates = false;
    this.saveChanges();
  }

  getTotalCosts(): number {
    return this.isMarkedForDeletion || (this.displayProductUpdates && this.relatedProductWasDeleted)
      ? 0
      : this.getEntryTotalCosts({
          pricePerUnit: this.displayProductUpdates
            ? this.latestProduct.pricePerUnit
            : this.currentElectricityMeterUnit.pricePerUnit,
          discountFactor: this.internalDiscountFactor,
          isAlternative: this.isAlternative}, 1);
  }

  private getElectricityMeterUnit(emuId: number): shared.Product {
    return this.electricityMeterUnits.find(emu => emu.id === emuId);
  }

  private getRelatedProductState(currentElectricityMeterUnit: shared.Product)
    : RelatedProductsState {
    const electricityMeterUnit = this.getElectricityMeterUnit(currentElectricityMeterUnit.id);
    return !electricityMeterUnit
      ? RelatedProductsState.IsDeleted
      : currentElectricityMeterUnit.version < electricityMeterUnit.version
        ? RelatedProductsState.IsOutdated
        : RelatedProductsState.IsUpToDate;
  }

  private onChangesSaved(): void {
    this.relatedProductState = this.getRelatedProductState(this.currentElectricityMeterUnit);
    super.endEdit();
  }

  private setCurrentElectricityMeterUnit(electricityMeterUnit: shared.Product, discountFactor?: number): void {
    this.currentElectricityMeterUnit = electricityMeterUnit;
    if (discountFactor !== undefined){
      this.internalDiscountFactor = discountFactor;
    }
    this.publishTotalCosts();
  }

  private publishTotalCosts(): void {
    const totalCost = this.getTotalCosts();
    this.totalCostsService.addOrUpdatePartialCosts(
      this.electricityMeterUnitInfo.emuEntry.id.toString(),
      totalCost);
  }
}
